import React, { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, CheckCircle2, XCircle,
  Target, TrendingUp, Link2, AlertTriangle,
} from 'lucide-react';
import type { MappingResult, MappingTableMapping, MappingColumnMapping, DictionaryResult } from '../../types';

interface Props {
  result: MappingResult;
  targetDictionary: DictionaryResult | null;
}

interface TargetCentricColumn {
  column_name: string;
  data_type: string;
  sources: Array<{
    source_table: string;
    source_column: string;
    confidence: number;
    transformation: string | null;
    notes: string | null;
  }>;
}

interface TargetCentricTable {
  table_name: string;
  columns: TargetCentricColumn[];
}

function buildTargetCentric(
  result: MappingResult,
  targetDict: DictionaryResult | null,
): TargetCentricTable[] {
  const index: Record<string, TargetCentricTable> = {};

  // Seed from target dictionary (shows unmapped targets too)
  if (targetDict?.tables) {
    for (const t of targetDict.tables) {
      index[t.table_name] = {
        table_name: t.table_name,
        columns: t.columns.map((c) => ({
          column_name: c.name,
          data_type: c.data_type,
          sources: [],
        })),
      };
    }
  }

  // Populate from mappings
  for (const m of result.mappings) {
    if (!m.target_table || m.mapping_type === 'NO_MAPPING') continue;
    if (!index[m.target_table]) {
      index[m.target_table] = { table_name: m.target_table, columns: [] };
    }
    const targetTable = index[m.target_table];
    for (const cm of m.column_mappings) {
      if (!cm.target_column) continue;
      let col = targetTable.columns.find((c) => c.column_name === cm.target_column);
      if (!col) {
        col = { column_name: cm.target_column, data_type: '—', sources: [] };
        targetTable.columns.push(col);
      }
      col.sources.push({
        source_table: m.source_table,
        source_column: cm.source_column,
        confidence: cm.confidence,
        transformation: cm.transformation,
        notes: cm.notes,
      });
    }
  }

  return Object.values(index);
}

const MappingResults: React.FC<Props> = ({ result, targetDictionary }) => {
  const tables = useMemo(() => buildTargetCentric(result, targetDictionary), [result, targetDictionary]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [detailFor, setDetailFor] = useState<TargetCentricColumn['sources'][0] | null>(null);

  const toggle = (name: string) => setExpanded((s) => ({ ...s, [name]: !s[name] }));

  const summary = result.summary;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard icon={<Target className="w-4 h-4" />} label="Target Columns" value={summary.total_target_columns} />
        <StatCard icon={<CheckCircle2 className="w-4 h-4 text-cargo-success" />} label="Mapped" value={summary.mapped_columns} />
        <StatCard icon={<AlertTriangle className="w-4 h-4 text-cargo-warning" />} label="Unmapped" value={summary.unmapped_columns} />
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Coverage" value={`${summary.coverage_percentage}%`} />
      </div>

      <div className="flex items-center gap-3 text-sm">
        <span className="text-cargo-muted">Avg. Confidence:</span>
        <span className="font-semibold text-cargo-text">{(summary.average_confidence * 100).toFixed(0)}%</span>
        <span className="text-cargo-muted">•</span>
        <span className="text-cargo-muted">{summary.total_mapping_options} mapping options</span>
      </div>

      {/* Target tables */}
      <div className="space-y-3">
        {tables.map((t) => {
          const mapped = t.columns.filter((c) => c.sources.length > 0).length;
          const total = t.columns.length;
          const pct = total ? Math.round((mapped / total) * 100) : 0;
          const open = expanded[t.table_name];
          return (
            <div key={t.table_name} className="bg-cargo-panel border border-cargo-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggle(t.table_name)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-cargo-hover/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {open ? <ChevronDown className="w-4 h-4 text-cargo-muted" /> : <ChevronRight className="w-4 h-4 text-cargo-muted" />}
                  <Target className="w-4 h-4 text-cargo-accent" />
                  <span className="font-mono text-sm text-cargo-text">{t.table_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-cargo-muted">{mapped}/{total} mapped</span>
                  <CoverageBar pct={pct} />
                </div>
              </button>

              {open && (
                <div className="border-t border-cargo-border">
                  <table className="w-full text-sm">
                    <thead className="bg-cargo-surface text-left text-cargo-muted text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-2">Target Column</th>
                        <th className="px-4 py-2">Data Type</th>
                        <th className="px-4 py-2">Source Mappings</th>
                        <th className="px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cargo-border">
                      {t.columns.map((col) => (
                        <tr key={col.column_name}>
                          <td className="px-4 py-2 font-mono text-xs text-cargo-text">{col.column_name}</td>
                          <td className="px-4 py-2 font-mono text-xs text-cargo-muted">{col.data_type}</td>
                          <td className="px-4 py-2">
                            {col.sources.length === 0 ? (
                              <span className="text-cargo-muted text-xs italic">— unmapped —</span>
                            ) : (
                              <div className="space-y-2">
                                {col.sources.map((s, i) => (
                                  <button
                                    key={i}
                                    onClick={() => setDetailFor(s)}
                                    className="w-full text-left px-2 py-1.5 rounded hover:bg-cargo-hover/30 transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Link2 className="w-3 h-3 text-cargo-muted flex-shrink-0" />
                                      <span className="font-mono text-xs text-cargo-text">{s.source_table}.{s.source_column}</span>
                                      <ConfidencePill conf={s.confidence} />
                                    </div>
                                    {s.notes && (
                                      <div className="mt-1 pl-5 text-[11px] text-cargo-muted leading-snug">
                                        {s.notes}
                                      </div>
                                    )}
                                    {s.transformation && (
                                      <div className="mt-1 pl-5 text-[10px] font-mono text-cargo-accent bg-cargo-accent/5 rounded px-1.5 py-0.5 inline-block">
                                        {s.transformation}
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {col.sources.length === 0 ? (
                              <XCircle className="w-4 h-4 text-cargo-muted" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-cargo-success" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {detailFor && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setDetailFor(null)}
        >
          <div
            className="bg-cargo-panel border border-cargo-border rounded-lg p-6 max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-cargo-text font-semibold mb-4 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Mapping Detail
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Source</div>
                <div className="font-mono text-cargo-text">{detailFor.source_table}.{detailFor.source_column}</div>
              </div>
              <div>
                <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Confidence</div>
                <ConfidencePill conf={detailFor.confidence} />
              </div>
              {detailFor.transformation && (
                <div>
                  <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Transformation</div>
                  <code className="block bg-cargo-surface px-3 py-2 rounded text-xs text-cargo-text font-mono">
                    {detailFor.transformation}
                  </code>
                </div>
              )}
              {detailFor.notes && (
                <div>
                  <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">AI Reasoning</div>
                  <p className="text-cargo-text text-xs leading-relaxed">{detailFor.notes}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setDetailFor(null)}
                className="px-4 py-2 text-cargo-muted hover:text-cargo-text text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: number | string }> = ({ icon, label, value }) => (
  <div className="bg-cargo-panel border border-cargo-border rounded-lg p-4">
    <div className="flex items-center gap-2 text-cargo-muted text-xs font-medium uppercase tracking-wider">
      {icon}
      {label}
    </div>
    <div className="text-2xl font-semibold text-cargo-text mt-1">{value}</div>
  </div>
);

const CoverageBar: React.FC<{ pct: number }> = ({ pct }) => {
  const color = pct >= 80 ? 'bg-cargo-success' : pct >= 60 ? 'bg-cargo-warning' : 'bg-cargo-error';
  return (
    <div className="w-24 h-1.5 bg-cargo-border/40 rounded-full overflow-hidden">
      <div className={`${color} h-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const ConfidencePill: React.FC<{ conf: number }> = ({ conf }) => {
  const pct = Math.round(conf * 100);
  const cls = pct >= 80
    ? 'text-cargo-success bg-cargo-success/10'
    : pct >= 60
    ? 'text-cargo-warning bg-cargo-warning/10'
    : 'text-cargo-muted bg-cargo-border/30';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>
      {pct}%
    </span>
  );
};

export default MappingResults;
